import { useState, useEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ModelApiItem } from "../../types/Model/ModelSchema";

type Props = {
  models: ModelApiItem[];
  current: ModelApiItem | null;
  onSelect: (item: ModelApiItem) => void;
  onClickAddInspectionToModel: (item: ModelApiItem) => void;
};

export default function ChecklistSidebar({ models, current, onSelect, onClickAddInspectionToModel }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const filteredModels = useMemo(() => {
    const trimmedSearch = debouncedSearch.trim();
    if (!trimmedSearch) return models;
    const lower = trimmedSearch.toLowerCase();
    return models.filter((item) =>
      item.modelNumber.toLowerCase().includes(lower)
    );
  }, [debouncedSearch, models]);

  const rowVirtualizer = useVirtualizer({
    count: filteredModels?.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    overscan: 10,
  });

  return (
    <div className="w-[300px] border-r bg-gray-50 flex flex-col">
      <div className="py-2 pr-1 bg-white">
        <input
          type="text"
          placeholder="Search models..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
      </div>

      <div ref={parentRef} className="flex-1 overflow-auto custom-scrollbar">
        {filteredModels?.length > 0 ? (
          <div
            style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: "relative" }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = filteredModels[virtualRow.index];
              const isModuleDisabled = item?.inspectionIds?.length === 0;
              const isItemSelected = current?.modelNumber === item.modelNumber;
              return (
                <div
                  key={item.modelNumber}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    display: "flex",
                    justifyContent: "space-between",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  onClick={() => !isModuleDisabled && onSelect(item)}
                  title={`${item.modelNumber}${item.modelDescription ? ` — ${item.modelDescription}` : ""}`}
                  className={`cursor-pointer px-4 py-2 border-b border-gray-100 hover:bg-orange-100 transition-colors items-center
                    ${isItemSelected ? "bg-orange-200 font-medium" : ""}
                    ${isModuleDisabled ? "text-gray-400 italic" : ""}`}>
                  <span className="flex-1 min-w-0 truncate">{item.modelNumber}</span>
                  {isModuleDisabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickAddInspectionToModel(item)
                      }}
                      className="ml-2 shrink-0 bg-orange-400 text-white px-2 py-1 rounded text-xs"
                    >
                      Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 p-4">No models available.</p>
        )}
      </div>
    </div>
  );
}